import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React, { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@shared/hooks/useAuth';
import { useUnreadCounts } from '@shared/hooks/message/useUnreadCounts';
import { useIsMobile } from '@shared/hooks/use-mobile';
import { getTranslatedNavItemsForRole, getTranslatedNavItemByPath } from '@shared/lib/navigation';
import { Building } from 'lucide-react';
import {
  SidebarProvider,
  SidebarInset
} from '@shared/ui/sidebar';
import { AppSidebar } from '@shared/layout/AppSidebar';
import { AppHeader } from '@shared/layout/AppHeader';
import { PatientMobileHeader } from '@shared/layout/PatientMobileHeader';
import { DoctorMobileHeader } from '@shared/layout/DoctorMobileHeader';
import { MobileBottomNav } from '@shared/layout/MobileBottomNav';
import { ErrorBoundary, LoadingSkeleton } from '@shared/components';

interface AppShellProps {
  children?: React.ReactNode;
}

const MESSAGING_ENABLED = import.meta.env.VITE_FEATURE_MESSAGING !== 'false';

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const { i18n } = useLingui();
  const isMobile = useIsMobile();

  // Get current page info for breadcrumbs and title
  const baseNavItems = getTranslatedNavItemsForRole((descriptor) => i18n._(descriptor), user?.role);
  const navItems = React.useMemo(() => {
    if (!user) return baseNavItems;
    let items = [...baseNavItems];

    const isOwner = user.role === 'doctor' && user.organization_roles?.includes('owner');
    if (isOwner && !items.find((item) => item.key === 'manage-organization')) {
      items = [
        ...items,
        {
          key: 'manage-organization',
          label: t({
            id: 'shared.layout.AppShell.label-manage-organization',
            message: 'Manage Organization',
          }),
          path: '/organization/manage',
          icon: Building,
          roles: ['doctor'],
          description: t({
            id: 'shared.layout.AppShell.description-organization-configuration-and-members',
            message: 'Organization configuration and members',
          }),
        },
      ];
    }

    return items;
  }, [baseNavItems, user]);

  const { unreadCounts, loading: unreadLoading } = useUnreadCounts({
    userId: user?.id,
    enabled: Boolean(user && MESSAGING_ENABLED),
  });

  const displayNavItems = React.useMemo(() => {
    const filtered = MESSAGING_ENABLED ? navItems : navItems.filter((item) => item.key !== 'messages');
    if (unreadLoading) return filtered;
    return filtered.map((item) =>
      item.key === 'messages' ? { ...item, badge: unreadCounts.unreadMessages } : item
    );
  }, [navItems, unreadCounts, unreadLoading]);

  const currentNavItem = React.useMemo(() => {
    const explicit = getTranslatedNavItemByPath(location.pathname, (descriptor) => i18n._(descriptor), user?.role);
    const sourceNav = displayNavItems;
    return explicit ?? sourceNav.find((item) => item.path === location.pathname);
  }, [displayNavItems, location.pathname, user?.role, i18n]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!user) {
    // This should not happen as AppShell is only rendered for authenticated users
    return null;
  }

  // Mobile layout for patients: floating bottom nav + compact header
  const isPatient = user.role === 'patient';
  if (isPatient && isMobile) {
    const isFullscreenPage = location.pathname.startsWith('/messages');
    const PATIENT_NAV_KEYS = ['dashboard', 'quotes', 'travel', 'payments', 'messages'] as const;

    return (
      <ErrorBoundary>
        <div className="flex flex-col min-h-screen bg-background">
          <PatientMobileHeader user={user} />
          <main
            className={
              isFullscreenPage
                ? 'flex-1 overflow-hidden'
                : 'flex-1 pb-24 overflow-y-auto p-3'
            }
          >
            <Suspense fallback={<LoadingSkeleton />}>
              {children || <Outlet />}
            </Suspense>
          </main>
          <MobileBottomNav
            navItems={displayNavItems}
            navKeys={PATIENT_NAV_KEYS}
            unreadCount={unreadCounts.unreadMessages}
          />
        </div>
      </ErrorBoundary>
    );
  }

  // Mobile layout for doctors: floating bottom nav + compact header
  const isDoctor = user.role === 'doctor';
  if (isDoctor && isMobile) {
    const isFullscreenPage = location.pathname.startsWith('/messages');
    const DOCTOR_NAV_KEYS = ['dashboard', 'patients', 'quotes', 'payments', 'messages'] as const;
    const DOCTOR_SHORT_LABELS = { dashboard: 'My Day' };

    return (
      <ErrorBoundary>
        <div className="flex flex-col min-h-screen bg-background">
          <DoctorMobileHeader user={user} />
          <main
            className={
              isFullscreenPage
                ? 'flex-1 overflow-hidden'
                : 'flex-1 pb-24 overflow-y-auto p-3'
            }
          >
            <Suspense fallback={<LoadingSkeleton />}>
              {children || <Outlet />}
            </Suspense>
          </main>
          <MobileBottomNav
            navItems={displayNavItems}
            navKeys={DOCTOR_NAV_KEYS}
            shortLabels={DOCTOR_SHORT_LABELS}
            unreadCount={unreadCounts.unreadMessages}
          />
        </div>
      </ErrorBoundary>
    );
  }

  // Default desktop/tablet layout with sidebar
  return (
    <ErrorBoundary>
      <SidebarProvider>
        <div className="flex h-screen w-full overflow-hidden">
          <AppSidebar user={user} navItems={displayNavItems} />
          <SidebarInset className="flex flex-col flex-1 min-w-0 overflow-hidden">
            <AppHeader
              user={user}
              currentPage={currentNavItem}
            />
            <main
              className={`flex-1 min-w-0 ${location.pathname.startsWith('/quotes') || location.pathname.startsWith('/messages')
                ? 'overflow-hidden'
                : 'space-y-4 p-2 sm:p-4 md:p-6 overflow-y-auto'
                }`}
            >
              <Suspense fallback={<LoadingSkeleton />}>
                {children || <Outlet />}
              </Suspense>
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </ErrorBoundary>
  );
};
